import { HOLDER_NAME_MAX_LENGTH, isValidIban, normalizeIban, singleLineText } from './iban.js';
import { assertCents, formatEuroPlain } from './money.js';

/**
 * GiroCode (EPC-QR) nach EPC069-12, Version 002: Der Inhalt des QR-Codes für „Überweisung per Banking-App scannen“.
 * Dieses Modul liefert nur den Text; gezeichnet wird der Code in der Oberfläche (Fehlerkorrektur M, UTF-8). BARSO überweist
 * nichts, die Person bestätigt die Überweisung in der Banking-App.
 *
 * Zeilen (jeweils durch einen Zeilenumbruch getrennt, ohne Umbruch am Ende):
 * `BCD`, `002`, `1` (UTF-8), `SCT`, BIC (leer, im EWR nicht nötig), Name (höchstens 70), IBAN, Betrag (`EUR12.30`), Zweck (leer),
 * Verwendungszweck mit Referenz (leer), Verwendungszweck als Text (höchstens 140). Zusammen höchstens 331 Byte.
 */

/** Kleinster und größter Betrag (0,01 € bis 999.999.999,99 €) laut EPC069-12. */
export const EPC_MIN_CENTS = 1;
export const EPC_MAX_CENTS = 99_999_999_999;
export const EPC_MAX_BYTES = 331;
export const EPC_TEXT_MAX_LENGTH = 140;

export interface EpcQrInput {
  /** Empfänger (Kontoinhaber). */
  name: string;
  iban: string;
  /** Ganze Cent, 1 bis 99.999.999.999. */
  cents: number;
  /** Verwendungszweck (Freitext), z. B. „Basar Frühjahr Nr. 17 Auszahlung“. */
  text: string;
}

/** Länge in Byte bei UTF-8 (ohne `TextEncoder`, das gibt es in der Umgebung des Pakets nicht). */
export function utf8Length(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}
const byteLength = utf8Length;

/**
 * Text des GiroCodes. Wirft bei ungültiger IBAN, leerem Namen oder einem Betrag außerhalb von 0,01 € bis 999.999.999,99 €.
 * Name und Text werden von Zeilenumbrüchen und Steuerzeichen befreit (zusätzlich zur Prüfung bei der Eingabe) und gekürzt,
 * bis alles in 331 Byte passt (zuerst der Text, dann der Name).
 */
export function buildEpcQrText(input: EpcQrInput): string {
  assertCents(input.cents);
  if (input.cents < EPC_MIN_CENTS || input.cents > EPC_MAX_CENTS) {
    throw new RangeError('Der Betrag muss zwischen 0,01 € und 999.999.999,99 € liegen.');
  }
  const iban = normalizeIban(input.iban);
  if (!isValidIban(iban)) throw new TypeError('Die IBAN ist ungültig.');
  let name = Array.from(singleLineText(input.name, HOLDER_NAME_MAX_LENGTH));
  if (name.length === 0) throw new TypeError('Der Name des Empfängers fehlt.');
  let text = Array.from(singleLineText(input.text, EPC_TEXT_MAX_LENGTH));
  const build = (): string =>
    [
      'BCD',
      '002',
      '1',
      'SCT',
      '',
      name.join(''),
      iban,
      `EUR${formatEuroPlain(input.cents)}`,
      '',
      '',
      text.join(''),
    ].join('\n');
  let result = build();
  while (byteLength(result) > EPC_MAX_BYTES && text.length > 0) {
    text = text.slice(0, -1);
    result = build();
  }
  while (byteLength(result) > EPC_MAX_BYTES && name.length > 1) {
    name = name.slice(0, -1);
    result = build();
  }
  return result;
}
